import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { format } from "date-fns";
import { setNewTaskVariables } from "../../../actions/task";
import { useWorkflowContext } from "../../../components/Workflow/WorkflowContextProvider";
import { isDateValid } from "../../../shared/validators/validator";
import { useEditorContext } from "../../editor.Context";
import { customVariablesClasses, generateUniqueId, smartChipClasses } from "../../editor.helper";
import "../taskDetail.scss";
import { useTaskVariableUpdates, useWorkflowVariableUpdates } from "./hooks/taskCustomVariable.hooks";
import { buildTaskCustomVariablePayloads, findExistingTaskCustomVariable, resolveTaskVariableDetails } from "./taskCustomVariable.utils";

/**
 * used to render date type component in task description variable
 * @author {Prachi Jain}
 */
const CustomVariableDates = ({ updateNonEditable, variableDetails, smartChipId, setShowEditChip, editorRef }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [dateError, setDateError] = useState("");
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { isNewTask, task, workflowCustomVariables } = useSelector((state) => state.taskSidebar);
  const location = useLocation();
  const parentRef = useRef();

  const { taskCustomVariables, setTaskCustomVariables, taskCustomVariablesRef } = useEditorContext();
  const { addTaskCustomVariables, addWorklowDescriptionCustomVariables } = useTaskVariableUpdates();
  const { updateSmartWorkflowVariables } = useWorkflowVariableUpdates();

  const workflowState = useWorkflowContext();

  const exisitingVariable = useMemo(
    () =>
      findExistingTaskCustomVariable({
        smartChipId,
        taskCustomVariables,
        workflowContextId: workflowState?.workflow?.Id,
      }),
    [smartChipId, taskCustomVariables, workflowState?.workflow?.Id]
  );

  const newVariableDetails = useMemo(
    () => resolveTaskVariableDetails(variableDetails, exisitingVariable, workflowCustomVariables),
    [variableDetails, exisitingVariable, workflowCustomVariables]
  );

  useEffect(() => {
    if (smartChipId) return setSelectedValue(exisitingVariable?.variableValue);
    setSelectedValue(newVariableDetails?.attributes?.defaultValue === "Current Date" ? new Date() : null);
  }, [smartChipId, newVariableDetails?.attributes]);

  /**
   * Handles the change event for the DatePicker. Validates the selected date to ensure it falls within the allowed min and max range.
   * If the date is out of range, sets an error message and prevents updating the value.
   * If the date is valid, updates the selected value and clears any error.
   * @author Bhavana
   */
  const updateNode = async (e) => {
    const chosenValue = e?.value;
    const minDate = newVariableDetails?.attributes?.startDate
      ? new Date(newVariableDetails?.attributes?.startDate)
      : !newVariableDetails?.attributes?.pastDates
      ? new Date()
      : null;
    const maxDate = newVariableDetails?.attributes?.endDate
      ? new Date(newVariableDetails?.attributes?.endDate)
      : !newVariableDetails?.attributes?.futureDates
      ? new Date()
      : null;

    if ((minDate && chosenValue < minDate) || (maxDate && chosenValue > maxDate)) {
      setDateError(
        `Please pick a date between ${minDate ? minDate?.toLocaleDateString() : "-"} and ${maxDate ? maxDate?.toLocaleDateString() : "-"}.`
      );
      return;
    }
    setDateError("");
    setSelectedValue(chosenValue);
    handleSmartChipSelection(chosenValue);
  };

  const handleSmartChipSelection = (chosenValue) => {
    const chipClass = newVariableDetails?.chipType === "smart chip" ? smartChipClasses[3] : customVariablesClasses[3];
    if (smartChipId && !workflowState?.workflow?.Id) {
      updateExistingSmartChip(chosenValue, chipClass);
    } else if (!smartChipId) {
      addNewSmartChip(chosenValue, chipClass);
    }
  };

  const updateExistingSmartChip = (selectedDate, chipClass) => {
    const updateChip = editorRef?.current?.element?.querySelector(`#${smartChipId}`);
    updateChip.innerHTML = format(selectedDate, newVariableDetails?.attributes?.dateFormat?.label ?? "dd/MM/yyyy");
    setShowEditChip(false);
    const taskCustomVariable = { ...exisitingVariable, variableValue: selectedDate };
    const newTaskCustomVariables = taskCustomVariables.map((item) => (item.taskVariableId === smartChipId ? { ...taskCustomVariable } : item));
    setTaskCustomVariables([...newTaskCustomVariables]);
    taskCustomVariablesRef.current = [...newTaskCustomVariables];
    updateSmartWorkflowVariables([...newTaskCustomVariables]);
    if (!isNewTask) addTaskCustomVariables([...newTaskCustomVariables]);
    else dispatch(setNewTaskVariables([...newTaskCustomVariables]));
  };

  const addNewSmartChip = async (selectedDate, chipClass) => {
    try {
      const date =
        selectedDate && isDateValid(selectedDate) ? format(selectedDate, newVariableDetails?.attributes?.dateFormat?.label ?? "dd/MM/yyyy") : null;
      const id = generateUniqueId(user.id, "customDate", workflowState?.workflow?.Id ? null : date.getTime());
      const inputValue = workflowState?.workflow?.Id ? variableDetails?.name : date ? date : variableDetails?.name;
      updateNonEditable(inputValue, id, chipClass);

      const { newTaskCustomVariable, newWorkflowCustomVariable } = buildTaskCustomVariablePayloads({
        variableDetails,
        userId: user.id,
        variableValue: date,
        generatedId: id,
      });

      /** works only when workflow page is opened but task sidebar is not open */
      if (location?.pathname?.includes("/workflow") && !isNewTask && !task?.taskId) {
        const updatedWorkflowCustomVariables = [...taskCustomVariables, newWorkflowCustomVariable];
        setTaskCustomVariables([...updatedWorkflowCustomVariables]);
        taskCustomVariablesRef.current = [...updatedWorkflowCustomVariables];
        updateSmartWorkflowVariables([...updatedWorkflowCustomVariables]);
        if (workflowState?.workflow?.Id) addWorklowDescriptionCustomVariables([...updatedWorkflowCustomVariables], workflowState?.workflow);
        else workflowState?.setworkflowDescriptionVariables([...updatedWorkflowCustomVariables]);
      } else {
        const updatedTaskCustomVariables = [...taskCustomVariables, newTaskCustomVariable];
        setTaskCustomVariables(updatedTaskCustomVariables);
        taskCustomVariablesRef.current = [...updatedTaskCustomVariables];
        updateSmartWorkflowVariables(updatedTaskCustomVariables); // remove smart chips
        if (!isNewTask) addTaskCustomVariables(updatedTaskCustomVariables);
        else dispatch(setNewTaskVariables(updatedTaskCustomVariables));
      }
    } catch (error) {
      setDateError("Invalid date");
      return;
    }
  };

  return (
    <div>
      {!!(workflowState?.workflow?.Id && !smartChipId) && handleSmartChipSelection(variableDetails?.attributes?.defaultValue)}
      {!workflowState?.workflow?.Id && (
        <div style={{ minWidth: "200px" }} className='dt-editor-menu'>
          <div className='d-flex gap-2 justify-content-between align-items-center p-2' id='editor-custom-date-chip'>
            <DatePicker
              id={`editor-custom-date-chip`}
              placeholder={newVariableDetails?.attributes?.placeholder}
              value={selectedValue ? new Date(selectedValue) : null}
              onChange={(e) => updateNode(e)}
              format={newVariableDetails?.attributes?.dateFormat?.label ?? "dd/MM/yyyy"}
              {...(newVariableDetails?.attributes?.startDate && { min: new Date(newVariableDetails?.attributes?.startDate) })}
              {...(!newVariableDetails?.attributes?.startDate && !newVariableDetails?.attributes?.pastDates && { min: new Date() })}
              {...(newVariableDetails?.attributes?.endDate && { max: new Date(newVariableDetails?.attributes?.endDate) })}
              {...(!newVariableDetails?.attributes?.endDate && !newVariableDetails?.attributes?.futureDates && { max: new Date() })}
            />
            {dateError && <div className='text-danger small ml-2 mb-1'>{dateError}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomVariableDates;
